// src/components/inbox/AdvisorThread.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Loader from "../Loader";
import MessageList from "../thread/MessageList";
import Composer from "../thread/Composer";
import LoadFailed from "./LoadFailed";
import { Button } from "../ui";
import { joinThread, leaveThread } from "../../shared/_helpers/socket";
import { readCurrentUser } from "../../shared/_helpers/currentUser";
import { PLAN_NOT_ACTIVE } from "../plan/planCopy";
import {
  getThreadMessages,
  markThreadRead,
  sendThreadMessage,
} from "../../shared/_services/thread.services";
import {
  getThread,
  takeOverThread,
} from "../../shared/_services/inbox.services";
import {
  clientTabsReset,
  openMessageReceived,
  openMessageSendFailed,
  openMessageSendRetried,
  openMessagesLoaded,
  openThreadCleared,
  openThreadFailed,
  openThreadLoaded,
  openThreadLoading,
  loadCounts,
  ownerChanged,
  summaryLastReplyPatched,
  lastReplyOf,
  rowMarkedRead,
  selectOpen,
  selectOpenClient,
  selectOpenMessagesOldestFirst,
  selectOpenRow,
} from "../../redux/inboxSlice";
import {
  COMPOSER_HELPER,
  LOAD_FAILED,
  TAKE_OVER,
  TAKE_OVER_DONE,
  YOUR_THREAD,
  composerPlaceholder,
  fullName,
  isWaiting,
  ownedThreadLabel,
  sendAsLabel,
  takeOverPrompt,
  threadHeaderLine,
} from "./inboxFormat";

const PAGE_SIZE = 50;

// The thread column (SP2 spec § Components). Loads the thread with
// GET /threads/:threadId — the read of record, which writes the server's
// ReadLog row — then its newest page, then marks it read, and holds the
// thread's socket room while it is open. The socket listeners that feed it
// ('message received', the room re-join) belong to InboxPage. A failed load
// is shown in place with Try again (R71).
export default function AdvisorThread({ threadId }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const open = useSelector(selectOpen);
  const allMessages = useSelector(selectOpenMessagesOldestFirst);
  const openClient = useSelector(selectOpenClient);
  const row = useSelector(selectOpenRow);
  const me = readCurrentUser()?.user || {};
  const viewer = useMemo(
    () => ({ kind: "advisor", _id: me._id ?? null }),
    [me._id],
  );
  const isOpen = open.threadId === threadId;
  // Until this thread's load has started, the store still holds the last
  // one: never show its client (M8).
  const client = isOpen ? openClient : null;
  const thread = isOpen ? open.thread : null;
  const messages = isOpen ? allMessages : [];
  const ready = isOpen && open.status === "ready";
  const [attempt, setAttempt] = useState(0);
  const [loadError, setLoadError] = useState(null);
  // Read when a send resolves, after this render may have gone stale (R68).
  const ownerRef = useRef(null);
  ownerRef.current = thread?.owner ?? null;
  const clientIdRef = useRef(null);
  clientIdRef.current = thread?.clientId ?? null;
  // Set once this open has dropped its client's cached tabs (R89).
  const tabsResetRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    dispatch(openThreadLoading(threadId));
    (async () => {
      try {
        const res = await getThread(threadId);
        if (cancelled) return;
        // R89: once per open — this component is keyed by threadId, so each
        // open (a new :threadId, or a return from another page) is a new
        // instance, while Try again reuses it and keeps the tabs it read.
        // Before openThreadLoaded, which is what mounts the sidebar.
        if (!tabsResetRef.current && res.data?.thread?.clientId) {
          tabsResetRef.current = true;
          dispatch(clientTabsReset(res.data.thread.clientId));
        }
        dispatch(openThreadLoaded(res.data));
        const page = await getThreadMessages(threadId, { limit: PAGE_SIZE });
        if (cancelled) return;
        dispatch(
          openMessagesLoaded({
            threadId,
            messages: page.data.messages,
            nextBefore: page.data.nextBefore,
          }),
        );
      } catch (err) {
        if (cancelled) return;
        dispatch(openThreadFailed());
        setLoadError(err?.response?.data?.message || null);
        return;
      }
      // Separate from the load: a failed mark-read must not disable the thread.
      // The badge is the server's count, so it is read again after (R78).
      markThreadRead(threadId)
        .then(() => {
          if (!cancelled) dispatch(rowMarkedRead(threadId));
          dispatch(loadCounts({ force: true }));
        })
        .catch(() => {});
    })();
    return () => {
      cancelled = true;
    };
  }, [threadId, attempt, dispatch]);

  // Leaving the thread (another row, or another page) clears it, so a return
  // visit starts from nothing (M8). The room is left by the effect below.
  useEffect(() => () => dispatch(openThreadCleared()), [dispatch]);

  useEffect(() => {
    if (!ready) return undefined;
    joinThread(threadId);
    return () => leaveThread(threadId);
  }, [ready, threadId]);

  const send = useCallback(
    async ({ text }) => {
      if (!text) return;
      const tempId = `tmp-${Date.now()}-${Math.random()}`;
      dispatch(
        openMessageReceived({
          tempId,
          _id: tempId,
          threadId,
          senderType: "advisor",
          sender: {
            _id: me._id,
            firstName: me.firstName,
            lastName: me.lastName,
            title: me.title,
            userType: "advisor",
          },
          kind: "text",
          handoffFrom: null,
          content: { text, attachments: [] },
          createdAt: new Date().toISOString(),
          pending: true,
        }),
      );
      try {
        const res = await sendThreadMessage(threadId, {
          text,
          attachments: [],
        });
        dispatch(openMessageReceived({ ...res.data.message, tempId }));
        dispatch(
          summaryLastReplyPatched({
            clientId: clientIdRef.current,
            lastReply: lastReplyOf(res.data.message),
          }),
        );
        // R68: the server's first-reply claim makes an unowned thread the
        // sender's. Show it now; on `thread updated` for this thread the
        // chip's refetch, or when the chip no longer lists it one quiet read
        // of All (R77), corrects the header if another advisor claimed it
        // first.
        if (!ownerRef.current)
          dispatch(
            ownerChanged({
              threadId,
              owner: {
                _id: me._id,
                firstName: me.firstName,
                lastName: me.lastName,
              },
              ownerIsMe: true,
            }),
          );
      } catch (err) {
        dispatch(openMessageSendFailed({ tempId, text }));
        // SP5 (R219, owner item 13): a reply to a client whose plan is not
        // active is refused 409 — say why, the bubble stays failed.
        if (err?.response?.status === 409)
          enqueueSnackbar(err.response.data?.message || PLAN_NOT_ACTIVE, {
            variant: "error",
          });
      }
    },
    [
      dispatch,
      enqueueSnackbar,
      threadId,
      me._id,
      me.firstName,
      me.lastName,
      me.title,
    ],
  );

  const retry = useCallback(
    (failed) => {
      // Advisor replies are text only, so the whole draft is the bubble's
      // text; read it before the retry dispatch removes the bubble.
      const text = failed.content?.text ?? "";
      dispatch(openMessageSendRetried(failed.tempId));
      send({ text, files: [] });
    },
    [dispatch, send],
  );

  const loadOlder = useCallback(async () => {
    if (!open.nextBefore) return;
    try {
      const page = await getThreadMessages(threadId, {
        before: open.nextBefore,
        limit: PAGE_SIZE,
      });
      dispatch(
        openMessagesLoaded({
          threadId,
          messages: page.data.messages,
          nextBefore: page.data.nextBefore,
          prepend: true,
        }),
      );
    } catch (err) {
      // The button stays; pressing it again retries (R71 voice).
      enqueueSnackbar(err?.response?.data?.message || LOAD_FAILED, {
        variant: "error",
      });
    }
  }, [dispatch, enqueueSnackbar, threadId, open.nextBefore]);

  // An owner record with no first name still reads as a person (M12).
  const ownerFirst = thread?.owner?.firstName || "Advisor";
  const takeOver = useCallback(async () => {
    // window.confirm, not bare confirm: CRA's ESLint rejects the bare global.
    if (!window.confirm(takeOverPrompt(ownerFirst))) return;
    try {
      const res = await takeOverThread(threadId);
      const updated = res.data.thread;
      dispatch(
        ownerChanged({
          threadId,
          owner: updated.owner,
          ownerIsMe: updated.ownerIsMe,
        }),
      );
      enqueueSnackbar(TAKE_OVER_DONE, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || LOAD_FAILED, {
        variant: "error",
      });
    }
  }, [dispatch, enqueueSnackbar, ownerFirst, threadId]);

  const line = threadHeaderLine({
    thread,
    messages,
    row,
    viewerId: me._id ?? null,
  });

  return (
    <section
      data-slot="inbox-col--thread"
      className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-bg-bg max-lg:tw-h-screen max-rail:tw-w-full"
    >
      <div className="tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-bg-surface tw-px-6 tw-pb-3 tw-pt-4">
        <h2 className="tw-m-0 tw-text-xl tw-font-bold tw-text-text">
          {fullName(client)}
        </h2>
        {line && (
          <p
            data-slot="inbox-thread-line"
            data-waiting={isWaiting(thread) ? "true" : undefined}
            className={
              isWaiting(thread)
                ? "tw-mb-0 tw-mt-1 tw-text-xs tw-font-semibold tw-text-warning"
                : "tw-mb-0 tw-mt-1 tw-text-xs tw-text-text-muted"
            }
          >
            {line}
          </p>
        )}
        {thread?.owner && (
          <p
            data-slot="inbox-owner-line"
            className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-text"
          >
            {thread.ownerIsMe ? (
              YOUR_THREAD
            ) : (
              <>
                <span>{`${ownedThreadLabel(ownerFirst)} · `}</span>
                <Button
                  variant="text"
                  size="sm"
                  className="tw-align-baseline"
                  onClick={takeOver}
                >
                  {TAKE_OVER}
                </Button>
              </>
            )}
          </p>
        )}
      </div>
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-px-6 tw-pb-4">
        {isOpen && open.status === "loading" && <Loader />}
        {isOpen && open.status === "error" && (
          <LoadFailed
            message={loadError}
            onRetry={() => setAttempt((n) => n + 1)}
          />
        )}
        <MessageList
          messages={messages}
          onLoadOlder={loadOlder}
          hasOlder={isOpen && !!open.nextBefore}
          onRetry={retry}
          viewer={viewer}
        />
        <Composer
          onSend={send}
          disabled={!ready}
          placeholder={composerPlaceholder(client?.firstName)}
          helperText={COMPOSER_HELPER}
          sendLabel={sendAsLabel(me.firstName)}
          allowAttach={false}
        />
      </div>
    </section>
  );
}
